/* global themeVars */

import { getParameters } from '../../js/front/analytics';
import { getGoSession, loadGoFramework, onGoReady } from '../../js/front/go-session';

const AUTH_HINT_KEY = 'mnl-go-auth';

document.querySelectorAll( '.b-header' ).forEach( ( block ) => {

	const body = document.body;
	const searchButton = block.querySelector( '.js-nav-search-toggle' );
	const searchContainer = block.querySelector( '.b-search-suggestions' );
	const signInLink = block.querySelector( '.header-signin' );
	const searchForm = block.querySelector( '.search-form' );
	const hamburger = block.querySelector( '.js-nav-hamburger' );
	const navContainer = block.querySelector( '.b-header-nav-container' );
	const nav = block.querySelector( '.b-header-navigation' );
	const submenuItems = nav?.querySelectorAll( '.b-header-navigation-item--has-submenu' ) || [];
	const headerOverlay = document.querySelector( '.js-header-overlay' );
	const notificationBar = document.querySelector( '.c-notification-bar' );
	const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
	const headerHeight = block.offsetHeight || 0;
	const notificationBarHeight = notificationBar?.getBoundingClientRect().height || 0;
	const mobileBreakpoint = parseInt( getComputedStyle( document.documentElement ).getPropertyValue( '--mobile-breakpoint' ).trim() ) || 1340;
	const mediaBreakPoint = window.matchMedia( `(max-width: ${mobileBreakpoint}px)` );
	let isMobile = block.offsetWidth < mobileBreakpoint;
	let resizeDebounceTimer;
	let isKeyboardSubmenuMode = false;
	let isKeyboardSearchMode = false;

	/**
	 * Check if interaction was keyboard-initiated or from a coarse pointer device.
	 *
	 * @param {MouseEvent} event Click event.
	 * @return {boolean} True for keyboard/coarse pointer activation.
	 */
	const isTouchOrKeyboardAction = ( event ) => event.detail === 0 || window.matchMedia( '(hover: none), (pointer: coarse)' ).matches;

	/**
	 * Check if current device supports precise hover interactions.
	 *
	 * @return {boolean} True when hover + fine pointer are available.
	 */
	const hasFineHoverPointer = () => window.matchMedia( '(hover: hover) and (pointer: fine)' ).matches;

	/**
	 * Check if desktop hover interactions should be enabled.
	 *
	 * @return {boolean} True when viewport is desktop and pointer supports hover.
	 */
	const isDesktopHoverEnabled = () => hasFineHoverPointer() && window.matchMedia( `(min-width: ${mobileBreakpoint}px)` ).matches;

	/**
	 * Track promotion_view for mega-menu promos after submenu is intentionally opened.
	 *
	 * @param {HTMLElement|null} submenu Opened submenu element.
	 * @return {void}
	 */
	const trackMegaMenuPromotionViews = ( submenu ) => {

		if ( ! submenu || ! window.MNLAnalytics ) {

			return;

		}

		submenu.querySelectorAll( '.js-header-promo-template-host[data-mnl-promotion-id]' ).forEach( ( promotionHost ) => {

			if ( promotionHost.dataset.templateMounted !== 'true' ) {

				return;

			}

			const parameters = getParameters( promotionHost );

			if ( ! parameters.promotion_id ) {

				return;

			}

			const key = `promotion_view:${ parameters.promotion_id }:${ parameters.promotion_location || '' }`;

			window.MNLAnalytics.trackOnce( key, 'promotion_view', parameters );

		} );

	};

	/**
	 * Sync search container height for smooth open/close animation.
	 *
	 * @return {void}
	 */
	const syncSearchContainerHeight = () => {

		if ( ! searchContainer ) {

			return;

		}

		const isActive = searchContainer.classList.contains( 'b-search-suggestions--active' );

		if ( ! isActive ) {

			return;

		}

		/**
		 * Measure current search panel height and update CSS variable.
		 *
		 * @return {void}
		 */
		const setSearchPanelHeight = () => {

			const panelHeight = Math.ceil( searchContainer.scrollHeight + 40 ); // Add extra padding to avoid cutting off content when scrollbars appear.

			searchContainer.style.setProperty( '--search-panel-max-height', `${panelHeight}px` );

		};

		requestAnimationFrame( setSearchPanelHeight );

	}

	/**
	 * Sync desktop submenu background panel height for smooth open/switch/close animation.
	 *
	 * @return {void}
	 */
	const syncDesktopSubmenuPanel = () => {

		if ( ! navContainer ) {

			return;

		}

		const activeSubmenu = nav?.querySelector( '.b-item-submenu--active' );

		if ( ! activeSubmenu || ! window.matchMedia( `(min-width: ${mobileBreakpoint}px)` ).matches ) {

			navContainer.style.setProperty( '--submenu-panel-height', '0px' );
			return;

		}

		/**
		 * Measure currently active submenu and update shared desktop panel height.
		 *
		 * @return {void}
		 */
		const setPanelHeight = () => {

			const submenuInner = activeSubmenu.querySelector( '.b-item-submenu__inner' );
			const panelHeight = Math.ceil( ( submenuInner || activeSubmenu ).getBoundingClientRect().height );

			navContainer.style.setProperty( '--submenu-panel-height', `${panelHeight}px` );

		};

		requestAnimationFrame( setPanelHeight );

	}

	// Set header height CSS variable on page load.
	if ( headerHeight ) {

		document.documentElement.style.setProperty( '--header-height', `${headerHeight}px` );

	}

	// Set top-bar height CSS variable on page load.
	if ( notificationBarHeight ) {

		document.documentElement.style.setProperty( '--top-bar-height', `${notificationBarHeight}px` );

	}

	block.addEventListener( 'click', ( event ) => {

		// Signed-in users: the sign-in button redirects to the account page.
		if ( event.target.closest( '.header-account' ) ) {

			event.preventDefault();
			window.location.href = '/account';

			return;

		}

		// Handle sign-in click and redirect to GO login page.
		if ( event.target.closest( '.header-signin' ) ) {

			event.preventDefault();
			const redirectLink = signInLink?.getAttribute( 'data-url' );

			if ( redirectLink ) {

				window.location.href = redirectLink;

			}

			return;

		}

		// Clear search input and focus on it when clicking the clear button.
		if ( event.target.closest( '.js-search-clear' ) && searchForm ) {

			const searchInput = searchForm.querySelector( '.search-form__input' );
			if ( searchInput ) {

				searchInput.value = '';
				searchInput.focus();
				searchInput.dispatchEvent( new Event( "input" ) );

			}

		}

		// Toggle active state of hamburger button and show/hide mobile navigation container.
		if ( event.target.closest( '.js-nav-hamburger' ) && navContainer ) {

			block.classList.toggle( 'b-header--mobile-menu-active' );
			hamburger.classList.toggle( 'header-hamburger--active' );
			navContainer.classList.toggle( 'b-header-nav-container--active' );

			if ( hamburger.classList.contains( 'header-hamburger--active' ) ) {

				hamburger.setAttribute( 'aria-expanded', 'true' );
				toggleSearch( searchContainer, searchButton, true );

			} else {

				hamburger.setAttribute( 'aria-expanded', 'false' );
				closeSubmenus();

			}

			updateBodyScroll();

		}

		// Close active submenu when clicking the mobile back button.
		if ( event.target.closest( '.js-nav-mobile-back' ) ) {

			const activeSubmenu = nav?.querySelector( '.b-item-submenu--active' );
			closeSubmenus( activeSubmenu );

		}

	} );

	// Exit keyboard submenu mode after an explicit mouse action on desktop.
	block.addEventListener( 'pointerdown', ( event ) => {

		if ( isDesktopHoverEnabled() && event.pointerType === 'mouse' ) {

			isKeyboardSubmenuMode = false;
			isKeyboardSearchMode = false;

		}

	} );

	// Close search container and submenus when clicking the search overlay.
	if ( headerOverlay ) {

		headerOverlay.addEventListener( 'click', () => {

			toggleSearch( searchContainer, searchButton, true );
			closeSubmenus();

		} );

	}

	/**
	 * Check if header interactive layer is active.
	 *
	 * @return {boolean} True when search/submenu is active (and optionally mobile menu).
	 */
	const isHeaderLayerActive = () => {

		const isSearchActive = searchContainer?.classList.contains( 'b-search-suggestions--active' );
		const isAnySubmenuActive = nav?.querySelector( '.b-item-submenu--active' ) !== null;
		const isMobileMenuActive = block.classList.contains( 'b-header--mobile-menu-active' );

		return isMobileMenuActive || isSearchActive || isAnySubmenuActive;

	}

	/**
	 * Render submenu template content when submenu is opened.
	 *
	 * @param {HTMLElement|null} submenu Submenu element.
	 * @return {void}
	 */
	const renderSubmenuTemplates = ( submenu ) => {

		if ( ! submenu ) {

			return;

		}

		submenu.querySelectorAll( '.js-header-promo-template-host' ).forEach( ( templateHost ) => {

			if ( templateHost.dataset.templateMounted === 'true' ) {

				return;

			}

			const template = templateHost.querySelector( 'template.js-header-promo-template' );

			if ( ! template ) {

				return;

			}

			templateHost.appendChild( template.content.cloneNode( true ) );
			templateHost.dataset.templateMounted = 'true';
			template.remove();

		} );

	}

	/**
	 * Focus the header search input after it's opened via hover or keyboard navigation,
	 * once the search panel has fully expanded and the fade-in animation has finished.
	 * (client request)
	 *
	 * While max-height is still animating from 0, the panel is fully clipped by
	 * overflow:hidden, so a plain focus() silently no-ops; wait for the transition
	 * to actually finish (with a timeout fallback for reduced-motion/no-transition cases).
	 *
	 * @param {HTMLElement} container - The search container element.
	 * @return {void}
	 */
	const focusSearchInputWhenReady = ( container ) => {

		const searchInput = container?.querySelector( 'input[type="search"]' );

		if ( ! searchInput ) {

			return;

		}

		let fallbackTimer = 0;

		/**
		 * Focus the input once, whichever trigger (transitionend or fallback) fires first.
		 *
		 * @param {TransitionEvent} [event] Transition event, when triggered by transitionend.
		 * @return {void}
		 */
		const doFocus = ( event ) => {

			if ( event && ( event.target !== container || event.propertyName !== 'max-height' ) ) {

				return;

			}

			clearTimeout( fallbackTimer );
			container.removeEventListener( 'transitionend', doFocus );
			searchInput.focus();

		};

		container.addEventListener( 'transitionend', doFocus );
		fallbackTimer = setTimeout( doFocus, 300 );

	}

	/**
	 * Toggle the search container's active state.
	 *
	 * @param {HTMLElement} container - The search container element.
	 * @param {HTMLElement} button - The search button element.
	 * @param {boolean} close - Whether to close the search container.
	 * @param {boolean} restoreFocusOnClose - Whether to return focus to toggle after closing.
	 *
	 * @return {void}
	 */
	const toggleSearch = ( container, button, close = false, restoreFocusOnClose = false ) => {

		if ( close ) {

			container?.classList.remove( 'b-search-suggestions--active' );

		} else {

			container?.classList.toggle( 'b-search-suggestions--active' );

		}

		const isActive = container?.classList.contains( 'b-search-suggestions--active' );
		button?.setAttribute( 'aria-expanded', isActive ? 'true' : 'false' );
		syncSearchContainerHeight();

		if ( isActive ) {

			focusSearchInputWhenReady( container );

		} else {

			if ( restoreFocusOnClose && button ) {

				button.focus();

			}

			isKeyboardSearchMode = false;
			syncDesktopSubmenuPanel();

		}

		headerOverlay?.classList.toggle( 'header-overlay--active', isHeaderLayerActive() );
		updateBodyScroll();

	}

	/**
	 * Close all active submenus except the one that was clicked.
	 *
	 * @param {HTMLElement} currentItemSubmenu - The submenu of the clicked item.
	 *
	 * @return {void}
	 */
	const closeSubmenus = ( currentItemSubmenu = null ) => {

		const activeSubmenus = nav?.querySelectorAll( '.b-item-submenu--active' );

		if ( activeSubmenus.length > 0 ) {

			activeSubmenus.forEach( ( submenu ) => {

				if ( submenu === currentItemSubmenu ) {

					return;

				}

				const submenuButton = submenu.closest( '.b-header-navigation-item' )?.querySelector( '.b-item-submenu-toggle' );

				submenu.classList.remove( 'b-item-submenu--active' );
				submenuButton?.setAttribute( 'aria-expanded', 'false' );
				submenu.setAttribute( 'aria-hidden', 'true' );

			} );

		}

		if ( currentItemSubmenu ) {

			const submenuButton = currentItemSubmenu.closest( '.b-header-navigation-item' )?.querySelector( '.b-item-submenu-toggle' );

			currentItemSubmenu.classList.toggle( 'b-item-submenu--active' );
			const isActive = currentItemSubmenu.classList.contains( 'b-item-submenu--active' );
			submenuButton?.setAttribute( 'aria-expanded', isActive ? 'true' : 'false' );
			currentItemSubmenu.setAttribute( 'aria-hidden', isActive ? 'false' : 'true' );

			if ( isActive ) {

				if ( isMobile && navContainer ) {

					navContainer.scrollTop = 0;

				}

				currentItemSubmenu.scrollTop = 0;
				renderSubmenuTemplates( currentItemSubmenu );
				trackMegaMenuPromotionViews( currentItemSubmenu );

			}

		}

		headerOverlay?.classList.toggle( 'header-overlay--active', isHeaderLayerActive() );
		syncDesktopSubmenuPanel();

		if ( ! nav?.querySelector( '.b-item-submenu--active' ) ) {

			isKeyboardSubmenuMode = false;

		}

		updateBodyScroll();

	}


	/**
	 * Add event listeners for search button and container to toggle search and close submenus on click or hover.
	 */
	if ( searchButton && searchContainer ) {

		// Works for mobile click, desktop hover and keyboard activation (Enter/Space).
		searchButton.addEventListener( 'click', ( event ) => {

			// Ignore desktop mouse click (hover handles it).
			if ( isDesktopHoverEnabled() && event.detail > 0 ) {

				return;

			}

			// Mark search as opened from keyboard, so hover won't close it until explicitly closed.
			if ( isDesktopHoverEnabled() && event.detail === 0 ) {

				isKeyboardSearchMode = true;

			}

			const isSearchActiveBeforeToggle = searchContainer.classList.contains( 'b-search-suggestions--active' );
			const shouldRestoreFocusOnClose = event.detail === 0 && isSearchActiveBeforeToggle;

			toggleSearch( searchContainer, searchButton, false, shouldRestoreFocusOnClose );
			closeSubmenus();

		} );

		[ searchButton, searchContainer ].forEach( ( searchHoverElement ) => {

			searchHoverElement.addEventListener( 'pointerenter', () => {

				// Run hover logic only on fine pointers, like mouse, to avoid accidental hover on touch devices.
				if ( ! isDesktopHoverEnabled() ) {

					return;

				}

				// Do not override keyboard-opened search with hover logic, to avoid closing it when user is navigating with keyboard.
				if ( isKeyboardSearchMode ) {

					return;

				}

				if ( ! searchContainer.classList.contains( 'b-search-suggestions--active' ) ) {

					toggleSearch( searchContainer, searchButton );
					closeSubmenus();

				}

			} );

			searchHoverElement.addEventListener( 'pointerleave', ( event ) => {

				// Run hover logic only on fine pointers, like mouse, to avoid accidental hover on touch devices.
				if ( ! isDesktopHoverEnabled() ) {

					return;

				}

				// Do not override keyboard-opened search with hover logic, to avoid closing it when user is navigating with keyboard.
				if ( isKeyboardSearchMode ) {

					return;

				}

				const nextElement = event.relatedTarget;

				// Keep search open when moving pointer between the toggle button and search panel.
				if ( nextElement instanceof Node && ( searchButton.contains( nextElement ) || searchContainer.contains( nextElement ) ) ) {

					return;

				}

				toggleSearch( searchContainer, searchButton, true );

			} );

		} );

	}

	/**
	 * Add event listeners for submenu items to toggle submenus on click or hover.
	 */
	submenuItems.forEach( ( item ) => {

		const submenu = item.querySelector( '.b-item-submenu' );
		const submenuButton = item.querySelector( '.b-item-submenu-toggle' );

		if ( ! submenu || ! submenuButton ) {

			return;

		}

		submenu.setAttribute( 'aria-hidden', submenu.classList.contains( 'b-item-submenu--active' ) ? 'false' : 'true' );

		// Works for mobile click, desktop hover and keyboard activation (Enter/Space).
		submenuButton.addEventListener( 'click', ( event ) => {

			// In desktop hover mode ignore mouse click; otherwise allow click toggle.
			if ( isDesktopHoverEnabled() && ! isTouchOrKeyboardAction( event ) ) {

				return;

			}

			// Mark submenu as opened from keyboard, so hover won't close it until explicitly closed.
			if ( isDesktopHoverEnabled() && event.detail === 0 ) {

				isKeyboardSubmenuMode = true;

			}

			closeSubmenus( submenu );
			toggleSearch( searchContainer, searchButton, true );

		} );

		item.addEventListener( 'pointerenter', () => {

			// Run hover logic only on fine pointers, like mouse, to avoid accidental hover on touch devices.
			if ( ! isDesktopHoverEnabled() ) {

				return;

			}

			// Do not override keyboard-opened submenu with hover logic, to avoid closing it when user is navigating with keyboard.
			if ( isKeyboardSubmenuMode ) {

				return;

			}

			closeSubmenus( submenu );
			toggleSearch( searchContainer, searchButton, true );

		} );

		item.addEventListener( 'pointerleave', ( event ) => {

			// Run hover logic only on fine pointers, like mouse, to avoid accidental hover on touch devices.
			if ( ! isDesktopHoverEnabled() ) {

				return;

			}

			// Do not override keyboard-opened submenu with hover logic, to avoid closing it when user is navigating with keyboard.
			if ( isKeyboardSubmenuMode ) {

				return;

			}

			const nextElement = event.relatedTarget;

			// Keep submenu/overlay stable when moving to another element inside navigation.
			if ( nextElement instanceof Node && nav?.contains( nextElement ) ) {

				return;

			}

			if ( submenu.classList.contains( 'b-item-submenu--active' ) ) {

				closeSubmenus();

			}

		} );

	} );

	/**
	 * Update body scroll state based on active elements.
	 *
	 * @return {void}
	 */
	const updateBodyScroll = () => {

		const isMobileMenuActive = block.classList.contains( 'b-header--mobile-menu-active' );
		const isSearchActive = searchContainer?.classList.contains( 'b-search-suggestions--active' );
		const isAnySubmenuActive = nav?.querySelector( '.b-item-submenu--active' ) !== null;
		const shouldLockBodyScroll = isMobileMenuActive || isSearchActive || isAnySubmenuActive;

		body.classList.toggle( 'header-no-scroll', shouldLockBodyScroll );
		notificationBar?.classList.toggle( 'c-notification-bar--hidden', shouldLockBodyScroll );

	}

	/**
	 * Update the --header-height CSS variable when the header height changes.
	 *
	 * @return {void}
	 */
	const updateHeaderStyles = () => {

		const height = block.offsetHeight || 0;
		const blockWidth = block.offsetWidth || 0;
		const topBarHeight = notificationBar?.getBoundingClientRect().height || 0;

		if ( height ) {

			document.documentElement.style.setProperty( '--header-height', `${ height }px` );

		}

		if ( topBarHeight ) {

			document.documentElement.style.setProperty( '--top-bar-height', `${topBarHeight}px` );

		}

		const wasMobile = isMobile;
		isMobile = blockWidth < mobileBreakpoint;
		syncDesktopSubmenuPanel();
		syncSearchContainerHeight();

		// Reset mobile menu state if we just transitioned from mobile to desktop.
		if ( ! isMobile && wasMobile ) {

			block.classList.remove( 'b-header--mobile-menu-active' );
			hamburger?.classList.remove( 'header-hamburger--active' );
			navContainer?.classList.remove( 'b-header-nav-container--active' );
			closeSubmenus();

		}

		navContainer?.style.removeProperty( 'transition' );

	};

	/**
	 * Observe the header block for resize events and update the header styles accordingly.
	 *
	 * @return {void}
	 */
	const resizeObserver = new ResizeObserver( () => {

		clearTimeout( resizeDebounceTimer );
		resizeDebounceTimer = setTimeout( updateHeaderStyles, 200 );

	} );

	resizeObserver.observe( block );

	/**
	 * Temporarily disable transitions during breakpoint change.
	 */
	mediaBreakPoint.addEventListener( 'change', () => {

		if ( navContainer ) {

			navContainer.style.transition = 'none';

		}

		clearTimeout( resizeDebounceTimer );
		resizeDebounceTimer = setTimeout( () => {

			if ( navContainer ) {

				navContainer.style.removeProperty( 'transition' );

			}

		}, 500 );

	} );

	/**
	 * Get currently focusable and visible elements inside the header block.
	 *
	 * @return {HTMLElement[]} Focusable elements.
	 */
	const getFocusableHeaderElements = () => {

		return Array.from( block.querySelectorAll( focusableSelector ) ).filter( ( element ) => {

			if ( element.getAttribute( 'aria-hidden' ) === 'true' ) {

				return false;

			}

			const styles = window.getComputedStyle( element );

			return styles.display !== 'none' && styles.visibility !== 'hidden' && element.getClientRects().length > 0;

		} );

	}

	/**
	 * Keep keyboard focus within the header while overlay is active.
	 *
	 * @param {KeyboardEvent} event Keyboard event.
	 * @return {void}
	 */
	const trapHeaderFocus = ( event ) => {

		if ( event.key === 'Escape' && isHeaderLayerActive() ) {

			const activeElement = block.ownerDocument.activeElement;
			const activeSubmenu = nav?.querySelector( '.b-item-submenu--active' );
			const activeSubmenuButton = activeSubmenu?.closest( '.b-header-navigation-item' )?.querySelector( '.b-item-submenu-toggle' );
			const shouldRestoreSubmenuParentFocus = !! ( activeSubmenu && activeSubmenuButton && activeElement instanceof Node && activeSubmenu.contains( activeElement ) );
			const isSearchActive = searchContainer?.classList.contains( 'b-search-suggestions--active' );

			toggleSearch( searchContainer, searchButton, true, isSearchActive );
			closeSubmenus();

			if ( shouldRestoreSubmenuParentFocus ) {

				activeSubmenuButton.focus();

			}

			if ( block.classList.contains( 'b-header--mobile-menu-active' ) ) {

				hamburger?.click();

			}

		}

		if ( event.key !== 'Tab' || ! isHeaderLayerActive() ) {

			return;

		}

		const focusableElements = getFocusableHeaderElements();

		if ( ! focusableElements.length ) {

			return;

		}

		const firstFocusableElement = focusableElements[ 0 ];
		const lastFocusableElement = focusableElements[ focusableElements.length - 1 ];
		const activeElement = block.ownerDocument.activeElement;

		if ( ! block.contains( activeElement ) ) {

			event.preventDefault();
			firstFocusableElement.focus();
			return;

		}

		if ( event.shiftKey && activeElement === firstFocusableElement ) {

			event.preventDefault();
			lastFocusableElement.focus();

			return;

		}

		if ( ! event.shiftKey && activeElement === lastFocusableElement ) {

			event.preventDefault();
			firstFocusableElement.focus();

		}

	};

	document.addEventListener( 'keydown', trapHeaderFocus );

	/**
	 * Display search prompts
	 */
	const displaySearchPrompts = () => {

		let timer;
		const searchInput = document.querySelector( '.search-form__input' );
		const searchPromptsContainer = searchForm.querySelector( '.search-suggestions-list' );
		const defaultPrompts = searchForm.querySelectorAll( '.search-suggestions-item' );
		const suggestionsLabel = searchForm.querySelector( '.search-form__suggestions-label' );

		/**
		 * Update suggestions label text between default suggestions and results.
		 *
		 * @param {boolean} showResultsLabel Whether to show results label.
		 * @return {void}
		 */
		const updateSuggestionsLabel = ( showResultsLabel ) => {

			if ( ! suggestionsLabel ) {

				return;

			}

			const nextLabel = showResultsLabel
				? suggestionsLabel.dataset.resultsLabel
				: suggestionsLabel.dataset.suggestionLabel;

			if ( nextLabel ) {

				suggestionsLabel.textContent = nextLabel;

			}

		};

		searchInput.addEventListener( 'input', ( e ) => {

			const key = e.target.value;
			const debounceGetSearchPrompt = debounce( () => getSearchPrompt( key, searchPromptsContainer ), 250 );

			clearTimeout( timer );
			timer = setTimeout( () => {

				if ( key.length === 0 ) {

					searchPromptsContainer.replaceChildren( ...defaultPrompts );
					updateSuggestionsLabel( false );
					syncSearchContainerHeight();

				}
				debounceGetSearchPrompt( key, searchPromptsContainer );

			}, 500 );

		} );

	}

	displaySearchPrompts();

	/**
	 * Gets Search prompt
	 * Performs AJAX request
	 *
	 * @param {string} key
	 * @param {Element} searchPromptsContainer
	 */
	const getSearchPrompt = ( key, searchPromptsContainer ) => {

		if ( searchPromptsContainer ) {

			searchPromptsContainer.innerContent = '';

		}

		if ( key.length > 0 ) {

			fetch( themeVars.ajaxUrl, {
				method: 'POST',
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				body: new URLSearchParams( {
					action: 'get_search_prompts',
					n: themeVars.security,
					searchKey: key,
				} )
			} )
				.then( ( response ) => response.json() )
				.then( ( data ) => {

					data.data.push( { title: 'View all results', link: `${themeVars.homeUrl}/?s=${key}` } );
					if ( Array.isArray( data.data ) && data.data.length > 0 ) {

						const listItems = [];

						data.data.forEach( ( prompt ) => {

							if ( typeof prompt === 'string' ) {

								prompt = JSON.parse( prompt );

							}

							const a = document.createElement( 'a' );
							const li = document.createElement( 'li' );
							const icon = document.createElement( 'img' );
							const label = document.createElement( 'span' );
							const searchIconLink = `${themeVars.templateUrl}/src/images/search.svg`;
							const arrowIconLink = `${themeVars.templateUrl}/src/images/arrow-right.svg`;
							const isArrowIcon = prompt.title === 'View all results';
							const iconLink = isArrowIcon ? arrowIconLink : searchIconLink;

							icon.setAttribute( 'src', iconLink );
							icon.setAttribute( 'alt', prompt.title );
							icon.classList.add( 'search-suggestions-item__icon' );
							label.textContent = prompt.title;
							label.classList.add( 'search-suggestions-item__label' );
							a.setAttribute( 'href', prompt.link );
							a.classList.add( 'search-suggestions-item__link' );
							a.appendChild( icon );
							a.appendChild( label );
							li.classList.add( 'search-suggestions-item' );
							li.appendChild( a );

							if( isArrowIcon ) {

								li.classList.add( 'search-suggestions-item--arrow' );

							}

							listItems.push( li );

						} );



						// Replacing all children (search results) at once with the generated array
						searchPromptsContainer.replaceChildren( ...listItems );
						const suggestionsLabel = searchForm.querySelector( '.search-form__suggestions-label' );
						const resultsLabel = suggestionsLabel?.dataset.resultsLabel;

						if ( resultsLabel && key.length > 0 ) {

							suggestionsLabel.textContent = resultsLabel;

						}
						syncSearchContainerHeight();

					}

				} )
				.catch( ( error ) => {

					// eslint-disable-next-line no-console
					console.error( 'Error:', error );

				} );

		}

	};

	/**
	 * Debounce
	 *
	 * @param {function} callback
	 * @param {integer} delay
	 * @return {(function(...[*]): void)|*}
	 */
	const debounce = ( callback, delay ) => {

		let timeoutId;
		/* eslint-disable func-names */
		return function( ...args ) {

			clearTimeout( timeoutId );
			timeoutId = setTimeout( () => {

				callback.apply( this, args );

			}, delay );

		};

	};

	/**
	 * Toggle header controls and body auth classes for the given auth state.
	 *
	 * @param {boolean} isAuthenticated Whether the player is signed in.
	 * @return {void}
	 */
	const setHeaderAuthState = ( isAuthenticated ) => {

		body.classList.toggle( 'go-authenticated', isAuthenticated );
		body.classList.toggle( 'go-unauthenticated', ! isAuthenticated );

		if ( ! signInLink ) {

			return;

		}

		if ( isAuthenticated ) {

			signInLink.classList.remove( 'header-signin' );
			signInLink.classList.add( 'header-account' );
			signInLink.dataset.mnlCtaName = 'header_account';

		} else {

			signInLink.classList.remove( 'header-account' );
			signInLink.classList.add( 'header-signin' );
			signInLink.dataset.mnlCtaName = 'header_sign_in';

		}

	};

	/**
	 * Apply GO session state to the header and remember it for the next page view.
	 *
	 * @param {Object|null} session Session object from go_fw.
	 * @return {void}
	 */
	const applyHeaderSessionState = ( session ) => {

		const isValid = !! ( session && session.isValid );

		setHeaderAuthState( isValid );

		try {

			window.localStorage.setItem( AUTH_HINT_KEY, isValid ? '1' : '0' );

		} catch {

			// Storage unavailable (e.g. blocked site data); the hint is optional.

		}

	};

	// GO loads after the page on most views, so show the last known auth state
	// until go_fw confirms or corrects it.
	try {

		if ( window.localStorage.getItem( AUTH_HINT_KEY ) === '1' ) {

			setHeaderAuthState( true );

		}

	} catch {

		// Storage unavailable; keep the default signed-out header.

	}

	/**
	 * Initialize all widgets after the original goReady logic.
	 */
	const initWidgets = () => {

		getGoSession().then( applyHeaderSessionState );

		// Keep header auth UI in sync when session changes after initial load.
		// eslint-disable-next-line no-undef, camelcase
		go_fw.onSessionStatusUpdate( applyHeaderSessionState );

	}

	onGoReady( initWidgets );

	// The Widgets block injects the loader immediately; this delayed call then does nothing.
	loadGoFramework( { defer: true } );

} );
